import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileInvoice,
  faSearch,
  faSync,
  faEye,
  faChevronLeft,
  faChevronRight,
  faSpinner,faPlus,
  faTimes,
  faCheckCircle,
  faClock,
  faExclamationTriangle,
  faCreditCard,
  faMoneyBillWave,
  faUser,
  faBuilding,
  faEnvelope,
  faPhone,
  faCalendarAlt,
  faDollarSign,
  faReceipt,
  faHistory,
  faPrint,
  faDownload,
  faLink
} from '@fortawesome/free-solid-svg-icons';
import '../styles/Invoices.css';

const Invoices = ({ embedded = false, onNavigate, setFormContent, onBreadcrumbUpdate }) => {
  // ==============================================
  // STATE MANAGEMENT
  // ==============================================
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    subscription_id: ''
  });
  
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [paymentData, setPaymentData] = useState({
    amount: '',
    payment_method: 'cash'
  });
  
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewInvoice, setViewInvoice] = useState(null);
  const [invoiceReceipts, setInvoiceReceipts] = useState([]);
  const [loadingReceipts, setLoadingReceipts] = useState(false);
  
  const [showCreateInvoiceModal, setShowCreateInvoiceModal] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  const [newInvoiceData, setNewInvoiceData] = useState({
    subscription_id: '',
    amount: ''
  });

  // ==============================================
  // AUTH & USER CONTEXT
  // ==============================================
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin_mechanicsap' || user?.role === 'admin_pos';

  // ==============================================
  // API HELPER FUNCTIONS
  // ==============================================
  const getAuthHeaders = () => {
    const authData = {
      user_id: user?.id || 1,
      username: user?.username || 'admin',
      role: user?.role || 'admin'
    };
    const authToken = btoa(JSON.stringify(authData));
    return {
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json'
    };
  };

  const apiRequest = async (url, options = {}) => {
    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          ...getAuthHeaders(),
          ...(options.headers || {})
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || data.message || 'API request failed');
      }
      
      return data;
    } catch (error) {
      console.error('API Request Error:', error);
      throw error;
    }
  };

  // ==============================================
  // FETCH FUNCTIONS
  // ==============================================
  
  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const queryParams = new URLSearchParams({
        action: 'get_invoices',
        page: pagination.page,
        limit: pagination.limit
      });
      
      if (filters.search) queryParams.append('search', filters.search);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.subscription_id) queryParams.append('subscription_id', filters.subscription_id);
      
      const data = await apiRequest(
        `https://ourzestapp.online/oz_backend/root_invoices.php?${queryParams.toString()}`
      );
      
      if (data.success) {
        setInvoices(data.data || []);
        setPagination(prev => ({
          ...prev,
          total: data.pagination?.total || 0,
          pages: data.pagination?.pages || 0
        }));
      } else {
        throw new Error(data.error || 'Failed to fetch invoices');
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters]);

  const fetchInvoiceReceipts = useCallback(async (invoiceId) => {
    setLoadingReceipts(true);
    try {
      const data = await apiRequest(
        `https://ourzestapp.online/oz_backend/root_invoices.php?action=get_receipts&invoice_id=${invoiceId}`
      );
      
      if (data.success) {
        setInvoiceReceipts(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching receipts:', error);
    } finally {
      setLoadingReceipts(false);
    }
  }, []);

  const fetchSubscriptionsForDropdown = useCallback(async () => {
    try {
      const data = await apiRequest(
        'https://ourzestapp.online/oz_backend/root_subscriptions.php?action=list&limit=1000'
      );
      
      if (data.success) {
        setSubscriptions(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
    }
  }, []);

  // ==============================================
  // INVOICE OPERATIONS
  // ==============================================
  
  const createInvoice = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const data = await apiRequest(
        'https://ourzestapp.online/oz_backend/root_invoices.php',
        {
          method: 'POST',
          body: JSON.stringify({
            action: 'create_invoice',
            subscription_id: newInvoiceData.subscription_id,
            created_by: user.id
          })
        }
      );
      
      if (data.success) {
        setSuccess(`Invoice #${data.data.invoice_no} created successfully! Amount: ${data.data.total_amount}`);
        setShowCreateInvoiceModal(false);
        setNewInvoiceData({ subscription_id: '', amount: '' });
        fetchInvoices();
        
        setTimeout(() => setSuccess(null), 5000);
      } else {
        throw new Error(data.error || 'Failed to create invoice');
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // ==============================================
  // PAYMENT OPERATIONS
  // ==============================================
  
  const recordPayment = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const data = await apiRequest(
        'https://ourzestapp.online/oz_backend/root_invoices.php',
        {
          method: 'POST',
          body: JSON.stringify({
            action: 'create_receipt',
            invoice_id: selectedInvoice?.id,
            amount: paymentData.amount,
            payment_method: paymentData.payment_method,
            received_by: user.id
          })
        }
      );
      
      if (data.success) {
        setSuccess(`Payment recorded! Receipt #${data.data.receipt_no} created. Invoice status: ${data.data.invoice_status}`);
        setShowPaymentModal(false);
        setPaymentData({ amount: '', payment_method: 'cash' });
        fetchInvoices();
        
        setTimeout(() => setSuccess(null), 5000);
      } else {
        throw new Error(data.error || 'Failed to record payment');
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // ==============================================
  // HELPER FUNCTIONS
  // ==============================================
  
  const getStatusColor = (status) => {
    const colors = {
      'paid': '#10b981',
      'partially_paid': '#f59e0b',
      'unpaid': '#ef4444',
      'overdue': '#dc2626',
      'cancelled': '#6b7280'
    };
    return colors[status] || '#6b7280';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'paid': 'Paid ✅',
      'partially_paid': 'Partially Paid ⏳',
      'unpaid': 'Unpaid ❌',
      'overdue': 'Overdue 🔴',
      'cancelled': 'Cancelled'
    };
    return labels[status] || status;
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      'cash': 'Cash',
      'bank_transfer': 'Bank Transfer',
      'credit_card': 'Credit Card',
      'debit_card': 'Debit Card',
      'online': 'Online Payment',
      'check': 'Check',
      'other': 'Other'
    };
    return labels[method] || method;
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  // ==============================================
  // HANDLERS
  // ==============================================
  
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleViewInvoice = async (invoice) => {
    setViewInvoice(invoice);
    await fetchInvoiceReceipts(invoice.id);
    setShowViewModal(true);
  };

  const handlePayment = (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentData({
      amount: invoice.due_amount || invoice.total_amount,
      payment_method: 'cash'
    });
    setShowPaymentModal(true);
  };

  const handlePaymentChange = (e) => {
    const { name, value } = e.target;
    setPaymentData(prev => ({ ...prev, [name]: value }));
  };

  const handleNewInvoiceChange = (e) => {
    const { name, value } = e.target;
    setNewInvoiceData(prev => ({ ...prev, [name]: value }));
    
    // Auto-fill amount when subscription selected
    if (name === 'subscription_id') {
      const selected = subscriptions.find(s => s.id === parseInt(value));
      if (selected) {
        setNewInvoiceData(prev => ({ ...prev, amount: selected.amount || '' }));
      }
    }
  };

  // ==============================================
  // EFFECTS
  // ==============================================
  
  useEffect(() => {
    fetchInvoices();
    fetchSubscriptionsForDropdown();
    
    if (onBreadcrumbUpdate) {
      onBreadcrumbUpdate('billing', 'invoices');
    }
  }, []);

  useEffect(() => {
    fetchInvoices();
  }, [pagination.page, filters]);

  // ==============================================
  // RENDER FUNCTIONS
  // ==============================================
  
  const renderStats = () => {
    const stats = invoices.reduce((acc, inv) => {
      if (inv.status === 'paid') acc.paid++;
      else if (inv.status === 'partially_paid') acc.partial++;
      else if (inv.status === 'unpaid') acc.unpaid++;
      else if (inv.status === 'overdue') acc.overdue++;
      acc.total++;
      acc.total_amount += parseFloat(inv.total_amount || 0);
      acc.paid_amount += parseFloat(inv.paid_amount || 0);
      acc.due_amount += parseFloat(inv.due_amount || 0);
      return acc;
    }, { paid: 0, partial: 0, unpaid: 0, overdue: 0, total: 0, total_amount: 0, paid_amount: 0, due_amount: 0 });
    
    return (
      <div className="invoice-stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#e8f5e9' }}>
            <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#388e3c' }} />
          </div>
          <div className="stat-info">
            <h3>{stats.paid}</h3>
            <p>Paid</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#fff3e0' }}>
            <FontAwesomeIcon icon={faClock} style={{ color: '#f57c00' }} />
          </div>
          <div className="stat-info">
            <h3>{stats.partial}</h3>
            <p>Partially Paid</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#ffebee' }}>
            <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#c62828' }} />
          </div>
          <div className="stat-info">
            <h3>{stats.unpaid}</h3>
            <p>Unpaid</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#fce4ec' }}>
            <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#dc2626' }} />
          </div>
          <div className="stat-info">
            <h3>{stats.overdue}</h3>
            <p>Overdue</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#e3f2fd' }}>
            <FontAwesomeIcon icon={faFileInvoice} style={{ color: '#1565c0' }} />
          </div>
          <div className="stat-info">
            <h3>{stats.total}</h3>
            <p>Total Invoices</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#e8f5e9' }}>
            <FontAwesomeIcon icon={faMoneyBillWave} style={{ color: '#2e7d32' }} />
          </div>
          <div className="stat-info">
            <h3>{formatCurrency(stats.total_amount)}</h3>
            <p>Total Amount</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#e8f5e9' }}>
            <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#059669' }} />
          </div>
          <div className="stat-info">
            <h3>{formatCurrency(stats.paid_amount)}</h3>
            <p>Total Paid</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#fce4ec' }}>
            <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#dc2626' }} />
          </div>
          <div className="stat-info">
            <h3>{formatCurrency(stats.due_amount)}</h3>
            <p>Total Due</p>
          </div>
        </div>
      </div>
    );
  };

  // ==============================================
  // MAIN RENDER
  // ==============================================
  
  return (
    <div className="invoices-management">
      {/* Header */}
      <div className="invoices-header">
        <div className="header-left">
          <h2>Invoices</h2>
          <span className="invoice-count">{pagination.total} total</span>
        </div>
        <div className="header-actions">
          <div className="search-box">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input
              type="text"
              name="search"
              placeholder="Search invoices..."
              value={filters.search}
              onChange={handleFilterChange}
            />
          </div>
          <select
            name="status"
            value={filters.status}
            onChange={handleFilterChange}
            className="filter-select"
          >
            <option value="">All Status</option>
            <option value="paid">Paid ✅</option>
            <option value="partially_paid">Partially Paid ⏳</option>
            <option value="unpaid">Unpaid ❌</option>
            <option value="overdue">Overdue 🔴</option>
          </select>
          <button className="btn btn-refresh" onClick={fetchInvoices}>
            <FontAwesomeIcon icon={faSync} /> Refresh
          </button>
          {isAdmin && (
            <button className="btn btn-primary" onClick={() => setShowCreateInvoiceModal(true)}>
              <FontAwesomeIcon icon={faPlus} /> New Invoice
            </button>
          )}
        </div>
      </div>
      
      {/* Alerts */}
      {error && (
        <div className="alert alert-error">
          <FontAwesomeIcon icon={faTimesCircle} />
          <span>{error}</span>
          <button className="alert-close" onClick={() => setError(null)}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}
      
      {success && (
        <div className="alert alert-success">
          <FontAwesomeIcon icon={faCheckCircle} />
          <span>{success}</span>
          <button className="alert-close" onClick={() => setSuccess(null)}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}
      
      {/* Stats */}
      {renderStats()}
      
      {/* Table */}
      <div className="invoice-table-container">
        <table className="invoice-table">
          <thead>
            <tr>
              <th>Invoice #</th>
              <th>Customer</th>
              <th>Application</th>
              <th>Invoice Date</th>
              <th>Due Date</th>
              <th>Total</th>
              <th>Paid</th>
              <th>Due</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="10" className="loading-cell">
                  <FontAwesomeIcon icon={faSpinner} spin />
                  <span>Loading invoices...</span>
                </td>
              </tr>
            ) : invoices.length === 0 ? (
              <tr>
                <td colSpan="10" className="empty-cell">
                  <p>No invoices found</p>
                  <button className="btn btn-primary" onClick={() => setShowCreateInvoiceModal(true)}>
                    <FontAwesomeIcon icon={faPlus} /> Create Invoice
                  </button>
                </td>
              </tr>
            ) : (
              invoices.map(inv => (
                <tr key={inv.id} className={inv.status === 'overdue' ? 'overdue-row' : ''}>
                  <td>
                    <span className="invoice-no">{inv.invoice_no}</span>
                  </td>
                  <td>
                    <div className="customer-info">
                      <div className="customer-avatar">
                        {inv.fullname?.charAt(0) || inv.username?.charAt(0) || 'U'}
                      </div>
                      <div className="customer-details">
                        <div className="customer-name">{inv.fullname || inv.username}</div>
                        <div className="customer-business">{inv.businessname || 'Individual'}</div>
                      </div>
                    </div>
                  </td>
                  <td>{inv.app_name || 'N/A'}</td>
                  <td>{formatDate(inv.invoice_date)}</td>
                  <td>{formatDate(inv.due_date)}</td>
                  <td className="amount-cell">{formatCurrency(inv.total_amount)}</td>
                  <td className="amount-cell paid">{formatCurrency(inv.paid_amount)}</td>
                  <td className="amount-cell due">{formatCurrency(inv.due_amount)}</td>
                  <td>
                    <span 
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(inv.status) }}
                    >
                      <FontAwesomeIcon 
                        icon={inv.status === 'paid' ? faCheckCircle : inv.status === 'overdue' ? faExclamationTriangle : faClock} 
                        className="status-icon"
                      />
                      {getStatusLabel(inv.status)}
                    </span>
                  </td>
                  <td className="actions-cell">
                    <button 
                      className="action-btn view-btn"
                      onClick={() => handleViewInvoice(inv)}
                      title="View Details"
                    >
                      <FontAwesomeIcon icon={faEye} />
                    </button>
                    <button 
                      className="action-btn print-btn"
                      onClick={() => window.print()}
                      title="Print Invoice"
                    >
                      <FontAwesomeIcon icon={faPrint} />
                    </button>
                    {inv.status !== 'paid' && inv.status !== 'cancelled' && (
                      <button 
                        className="action-btn payment-btn"
                        onClick={() => handlePayment(inv)}
                        title="Record Payment"
                      >
                        <FontAwesomeIcon icon={faCreditCard} />
                      </button>
                    )}
                    {inv.subscription_id && (
                      <button 
                        className="action-btn link-btn"
                        onClick={() => {
                          if (onNavigate) {
                            onNavigate('billing', 'managebillings');
                          }
                        }}
                        title="View Subscription"
                      >
                        <FontAwesomeIcon icon={faLink} />
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {/* Pagination */}
      <div className="pagination-container">
        <div className="pagination-info">
          Showing {invoices.length} of {pagination.total} invoices
        </div>
        <div className="pagination-controls">
          <button
            className="pagination-btn"
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
            disabled={pagination.page <= 1}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <span className="page-info">
            Page {pagination.page} of {pagination.pages || 1}
          </span>
          <button
            className="pagination-btn"
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
            disabled={pagination.page >= pagination.pages}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      </div>
      
      {/* ===== Create Invoice Modal ===== */}
      {showCreateInvoiceModal && (
        <div className="modal-overlay" onClick={() => setShowCreateInvoiceModal(false)}>
          <div className="modal-content create-invoice-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create New Invoice</h3>
              <button className="modal-close" onClick={() => setShowCreateInvoiceModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <form onSubmit={createInvoice}>
              <div className="form-row">
                <div className="form-group">
                  <label>Select Subscription *</label>
                  <select
                    name="subscription_id"
                    value={newInvoiceData.subscription_id}
                    onChange={handleNewInvoiceChange}
                    required
                  >
                    <option value="">Select a subscription</option>
                    {subscriptions.map(sub => (
                      <option key={sub.id} value={sub.id}>
                        {sub.customer_fullname || sub.customer_username} - {sub.app_name} ({formatCurrency(sub.amount)})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Amount</label>
                  <input
                    type="text"
                    name="amount"
                    value={newInvoiceData.amount}
                    onChange={handleNewInvoiceChange}
                    placeholder="Amount will auto-fill"
                    disabled
                  />
                  <small>Amount will be taken from the subscription</small>
                </div>
              </div>
              
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateInvoiceModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : null}
                  <FontAwesomeIcon icon={faFileInvoice} /> Generate Invoice
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* ===== Payment Modal ===== */}
      {showPaymentModal && (
        <div className="modal-overlay" onClick={() => setShowPaymentModal(false)}>
          <div className="modal-content payment-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Record Payment</h3>
              <button className="modal-close" onClick={() => setShowPaymentModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            {selectedInvoice && (
              <div className="invoice-summary">
                <div className="summary-item">
                  <span className="label">Invoice:</span>
                  <span className="value">{selectedInvoice.invoice_no}</span>
                </div>
                <div className="summary-item">
                  <span className="label">Customer:</span>
                  <span className="value">{selectedInvoice.fullname || selectedInvoice.username}</span>
                </div>
                <div className="summary-item">
                  <span className="label">Total Amount:</span>
                  <span className="value">{formatCurrency(selectedInvoice.total_amount)}</span>
                </div>
                <div className="summary-item">
                  <span className="label">Paid Amount:</span>
                  <span className="value">{formatCurrency(selectedInvoice.paid_amount)}</span>
                </div>
                <div className="summary-item">
                  <span className="label">Due Amount:</span>
                  <span className="value" style={{ color: '#ef4444' }}>
                    {formatCurrency(selectedInvoice.due_amount)}
                  </span>
                </div>
              </div>
            )}
            
            <form onSubmit={recordPayment}>
              <div className="form-row">
                <div className="form-group">
                  <label>Payment Amount *</label>
                  <input
                    type="number"
                    name="amount"
                    value={paymentData.amount}
                    onChange={handlePaymentChange}
                    placeholder="Enter amount"
                    step="0.01"
                    required
                    min="0.01"
                    max={selectedInvoice?.due_amount}
                  />
                  <small>Max: {formatCurrency(selectedInvoice?.due_amount)}</small>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Payment Method *</label>
                  <select
                    name="payment_method"
                    value={paymentData.payment_method}
                    onChange={handlePaymentChange}
                    required
                  >
                    <option value="cash">Cash</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="credit_card">Credit Card</option>
                    <option value="debit_card">Debit Card</option>
                    <option value="online">Online Payment</option>
                    <option value="check">Check</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowPaymentModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-success" disabled={loading}>
                  {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : null}
                  <FontAwesomeIcon icon={faCreditCard} /> Record Payment & Generate Receipt
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* ===== View Modal ===== */}
      {showViewModal && viewInvoice && (
        <div className="modal-overlay" onClick={() => setShowViewModal(false)}>
          <div className="modal-content view-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Invoice Details - {viewInvoice.invoice_no}</h3>
              <button className="modal-close" onClick={() => setShowViewModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <div className="view-content">
              {/* Customer Info */}
              <div className="view-section">
                <h4><FontAwesomeIcon icon={faUser} /> Customer Information</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="label">Name:</span>
                    <span className="value">{viewInvoice.fullname || viewInvoice.username}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Business:</span>
                    <span className="value">{viewInvoice.businessname || 'Individual'}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Email:</span>
                    <span className="value">{viewInvoice.email || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Phone:</span>
                    <span className="value">{viewInvoice.phonenumber || 'N/A'}</span>
                  </div>
                </div>
              </div>
              
              {/* Invoice Info */}
              <div className="view-section">
                <h4><FontAwesomeIcon icon={faFileInvoice} /> Invoice Information</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="label">Application:</span>
                    <span className="value">{viewInvoice.app_name || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Plan:</span>
                    <span className="value">{viewInvoice.plan_type || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Invoice Date:</span>
                    <span className="value">{formatDate(viewInvoice.invoice_date)}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Due Date:</span>
                    <span className="value">{formatDate(viewInvoice.due_date)}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Total Amount:</span>
                    <span className="value">{formatCurrency(viewInvoice.total_amount)}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Paid Amount:</span>
                    <span className="value">{formatCurrency(viewInvoice.paid_amount)}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Due Amount:</span>
                    <span className="value" style={{ color: '#ef4444' }}>
                      {formatCurrency(viewInvoice.due_amount)}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="label">Status:</span>
                    <span 
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(viewInvoice.status) }}
                    >
                      <FontAwesomeIcon 
                        icon={viewInvoice.status === 'paid' ? faCheckCircle : viewInvoice.status === 'overdue' ? faExclamationTriangle : faClock} 
                        className="status-icon"
                      />
                      {getStatusLabel(viewInvoice.status)}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Receipts */}
              <div className="view-section">
                <h4><FontAwesomeIcon icon={faReceipt} /> Payment History</h4>
                {loadingReceipts ? (
                  <div className="loading-receipts">
                    <FontAwesomeIcon icon={faSpinner} spin />
                    <span>Loading receipts...</span>
                  </div>
                ) : invoiceReceipts.length === 0 ? (
                  <p className="empty-receipts">No payments recorded yet</p>
                ) : (
                  <table className="receipts-table">
                    <thead>
                      <tr>
                        <th>Receipt #</th>
                        <th>Amount</th>
                        <th>Method</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoiceReceipts.map(receipt => (
                        <tr key={receipt.id}>
                          <td>{receipt.receipt_no}</td>
                          <td className="amount-cell">{formatCurrency(receipt.amount_received)}</td>
                          <td>{getPaymentMethodLabel(receipt.payment_method)}</td>
                          <td>{formatDate(receipt.payment_date)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              
              {/* Actions */}
              <div className="view-actions">
                {viewInvoice.status !== 'paid' && viewInvoice.status !== 'cancelled' && (
                  <button 
                    className="btn btn-success"
                    onClick={() => {
                      setShowViewModal(false);
                      handlePayment(viewInvoice);
                    }}
                  >
                    <FontAwesomeIcon icon={faCreditCard} /> Record Payment
                  </button>
                )}
                <button className="btn btn-secondary" onClick={() => setShowViewModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invoices;